import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gifImg from "../../Assets/avatar-cutout.gif";
import pngImg from "../../Assets/avatar-cutout.png";
import { FaWhatsapp, FaPaperPlane, FaTimes } from "react-icons/fa";

const TALK_MESSAGE =
  process.env.REACT_APP_TALK_MESSAGE || "👋 Click me to chat";
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "";
const GEMINI_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";

function ChatBot() {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState(false);
  const [msg, setMsg] = useState("");
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [avatarSrc, setAvatarSrc] = useState(gifImg);
  const shownRef = useRef(false);
  const listRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (shownRef.current) return;
      if (window.scrollY > 300) {
        shownRef.current = true;
        setShow(true);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!show) return;
    let i = 0;
    setMsg("");
    const timer = setInterval(() => {
      i++;
      setMsg(TALK_MESSAGE.slice(0, i));
      if (i >= TALK_MESSAGE.length) {
        clearInterval(timer);
        setSaid(true);
      }
    }, 40);
    return () => clearInterval(timer);
  }, [show]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  const openChat = () => {
    setOpen(true);
    if (messages.length === 0) {
      setTimeout(() => {
        setTyping(true);
        setTimeout(() => {
          setTyping(false);
          setMessages([
            {
              from: "bot",
              text:
                "Hi! I'm AR's assistant. Ask me about his skills, projects, experience — or say 'WhatsApp' to chat with him directly.",
            },
          ]);
        }, 900);
      }, 400);
    }
  };

  const getGeminiReply = async (currentText, history) => {
    try {
      const contents = [
        {
          role: "user",
          parts: [
            {
              text: `System instruction: You are a helpful assistant for the portfolio of Attique Ur Rehman, a full-stack web developer with 4+ years of experience in Javascript, Python & PHP, skilled in ReactJS, ExpressJS, Django, Web scraping, DevOps, AI & Automation. Keep answers short, friendly, and conversational.`,
            },
          ],
        },
        {
          role: "model",
          parts: [
            {
              text: "Got it! I am ready to talk about Attique's skills, projects, and experience.",
            },
          ],
        },
      ];

      for (const m of history) {
        contents.push({
          role: m.from === "me" ? "user" : "model",
          parts: [{ text: m.text }],
        });
      }

      contents.push({
        role: "user",
        parts: [{ text: currentText }],
      });

      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash-lite:generateContent?key=${GEMINI_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contents }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error("Gemini API error:", data);
        return `Gemini Error: ${data?.error?.message || res.statusText}`;
      }
      return data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
    } catch (e) {
      console.error("Gemini fetch exception:", e);
      return "";
    }
  };

  const getQuickReply = (text) => {
    const t = text.toLowerCase();
    if (/(skill|tech|stack|know|language|technology)/.test(t)) {
      return "I'm fluent in Javascript, Python & PHP. I build with ReactJS, ExpressJS, Django, and I'm into Web scraping, DevOps, AI & Automation. 💻";
    }
    if (/(project|work|built|portfolio|made)/.test(t)) {
      return "I've built web apps, Android apps, and SaaS products — from a Moonflix movies site to VPN & video downloader apps. Check the Projects section above! 🚀";
    }
    if (/(experience|year|how long|background)/.test(t)) {
      return "I've been in the field for 4+ years, building Web Apps & Products, plus AI, automation and DevOps work. 🛠️";
    }
    if (/(contact|reach|hire|email|phone|whatsapp|message|work with|freelance|available)/.test(t)) {
      return "You can reach me directly! Say 'WhatsApp' and I'll open a chat for you, or use the contact form on the site. 📩";
    }
    if (/(whatsapp|wa|chat with)/.test(t)) {
      return "Sure! Click the WhatsApp button below and message me directly. 💬";
    }
    if (/(hi|hello|hey|salam|salaam|assalam)/.test(t)) {
      return "Hey there! 👋 Great to see you. Ask me about my skills, projects, or how to reach me.";
    }
    if (/(thank|thanks|shukria)/.test(t)) {
      return "You're welcome! 🙌 Happy to help.";
    }
    if (/(name|who are you|your name)/.test(t)) {
      return "I'm the assistant bot for Attique Ur Rehman's portfolio. 🤖";
    }
    return null;
  };

  const send = (preset) => {
    const text = (preset || input).trim();
    if (!text) return;
    setMessages((m) => [...m, { from: "me", text }]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      if (/whatsapp|whats app|whats-app|chat on wa|open whatsapp/i.test(text)) {
        openWhatsApp(text);
        setTyping(false);
        setMessages((m) => [
          ...m,
          {
            from: "bot",
            text: "Opening WhatsApp for you… 👋 If the window didn't pop up, click the green button below.",
          },
        ]);
        return;
      }

      const quick = getQuickReply(text);
      if (quick) {
        setTyping(false);
        setMessages((m) => [...m, { from: "bot", text: quick }]);
        return;
      }

      if (GEMINI_KEY) {
        getGeminiReply(text, messages).then((r) => {
          setTyping(false);
          setMessages((m) => [
            ...m,
            {
              from: "bot",
              text:
                r ||
                "Good question! 🤔 Hit the WhatsApp button below to chat with me directly.",
            },
          ]);
        });
        return;
      }

      setTyping(false);
      setMessages((m) => [
        ...m,
        {
          from: "bot",
          text:
            "Good question! 🤔 I can tell you about my skills, projects, and experience. For anything else, hit the WhatsApp button below.",
        },
      ]);
    }, 800);
  };

  const waLink = (text) => {
    const num = (WHATSAPP_NUMBER || "923033302117").replace(/\D/g, "");
    const body = text
      ? `Hi Attique! I just chatted with your AI assistant on your portfolio.\n\n${text}`
      : "Hi Attique! I found your portfolio and wanted to reach out.";
    return `https://wa.me/${num}?text=${encodeURIComponent(body)}`;
  };

  const openWhatsApp = (text) => {
    window.open(waLink(text), "_blank", "noopener,noreferrer");
  };

  const lastUserMsg = [...messages].reverse().find((m) => m.from === "me")?.text;

  const onClose = () => setShow(false);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={`chat-popup ${open ? "open" : ""}`}
          initial={{ y: 360, opacity: 0, scale: 0.7 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 360, opacity: 0, scale: 0.7 }}
          transition={{ type: "spring", stiffness: 90, damping: 12, mass: 1.2 }}
        >
          <AnimatePresence>
            {open && (
              <motion.div
                className="chat-window"
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.9 }}
                transition={{ duration: 0.25 }}
              >
                <div className="chat-header">
                  <img src={avatarSrc} onError={() => setAvatarSrc(pngImg)} className="chat-header-avatar" alt="me" />
                  <div className="chat-header-info">
                    <span className="chat-header-name">AR</span>
                    <span className="chat-header-status">
                      <span className="chat-dot" /> Online
                    </span>
                  </div>
                  <button
                    className="chat-window-close"
                    onClick={() => setOpen(false)}
                    aria-label="close"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="chat-body" ref={listRef}>
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={`chat-msg ${m.from === "me" ? "me" : "bot"}`}
                    >
                      <span className="chat-bubble">{m.text}</span>
                    </div>
                  ))}
                  {typing && (
                    <div className="chat-msg bot">
                      <span className="chat-bubble typing">
                        <span className="tdot" />
                        <span className="tdot" />
                        <span className="tdot" />
                      </span>
                    </div>
                  )}
                </div>
                <div className="chat-wa-bar">
                  <button
                    onClick={() => openWhatsApp(lastUserMsg)}
                    className="chat-wa-btn"
                  >
                    <FaWhatsapp /> Chat on WhatsApp
                  </button>
                </div>
                <div className="chat-input-row">
                  <input
                    className="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Type a message..."
                  />
                  <button className="chat-send" onClick={() => send()}>
                    <FaPaperPlane />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!open && (
            <>
              <div className="popup-bubble">
                <span className="popup-text">{msg}</span>
                {said && <span className="popup-cursor">|</span>}
                <button className="popup-close" onClick={onClose} aria-label="close">
                  &times;
                </button>
              </div>
              <motion.div
                className="popup-avatar-wrap"
                animate={show && !said ? { rotate: [0, -2, 2, -1, 1, 0] } : {}}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                onClick={openChat}
              >
                <img src={avatarSrc} onError={() => setAvatarSrc(pngImg)} className="popup-avatar" alt="me" />
              </motion.div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ChatBot;
